import React from 'react'
import { css, cx } from '@emotion/css'
import { FormattedMessage, useIntl } from 'react-intl'
import { MultiSelectCombobox } from '../../legacy-design-system/combobox'
import { Row, Box } from '../../legacy-design-system/form'
import {
  allRuleSeverities,
  normalizeAssessRulesToTypedVulnerabilities,
  normalizeEnvironmentsToTypedServers,
  normalizeRuleSeveritiesToTypedVulnerabilities,
  normalizeStatusesToTypedVulnerabilities,
  normalizeTagsToTypedServers,
  normalizeTagsToTypedVulnerabilities,
  TypedServers,
  TypedVulnerabilities
} from '../shared'
import { defaultStatusRegistry } from '../vulnerability-status'
import { Checkbox } from '../../legacy-design-system/checkbox'
import { metadataCss } from '../../legacy-design-system/text'
import { I18nConsumer } from '../i18n-provider'
import { allServerEnvironments } from '../server-environment'
import {
  theme,
  IconInformation,
  IconWarning
} from '@contrast-security-inc/design-system-foundations'
import { BaseAssessRule } from '../assess-rules'
import { Policy } from '../policy'
import { Link } from '@contrast-security-inc/design-system-typography'

const errorStateMessageCss = css`
  background-color: rgba(234, 85, 77, 0.15);
  border-radius: 4px;
  display: inline-flex;
  padding: 16px;
  margin-top: 10px;
`

const iconCss = { paddingRight: '3px', verticalAlign: 'middle' }

export const testIds = {
  vulnLimitMsg: 'vuln-limit-info-message',
  configMissingMsg: 'report-config-missing-message'
}

interface ComplianceReport {
  readonly id: string
  readonly name: string
}

export type ErrorLimit =
  | 'VULS_LIMIT_ERROR'
  | 'ROUTE_LIMIT_ERROR'
  | 'VULNS_AND_ROUTES_LIMIT_ERROR'
export interface LimitsReportError {
  readonly errorType: ErrorLimit
  readonly error: string
  readonly limit: number
  readonly total: number
}

export interface AttestationFormProps {
  isReportConfigMissing: boolean
  isVulnDetailsEnabled: boolean
  limitErrors: LimitsReportError[]
  isRouteObservationsEnabled: boolean

  vulnRules: BaseAssessRule[]
  vulnTags: string[]

  serverTags: string[]
  compliancePolicies: Policy[]

  vulnsSelection: TypedVulnerabilities[]
  serversSelection: TypedServers[]
  complianceReportsSelection: ComplianceReport[]

  vulnTotal: number
  totalRoutes: number

  onChangeVulns: (
    severities: TypedVulnerabilities[],
    rules: TypedVulnerabilities[],
    statuses: TypedVulnerabilities[],
    tags: TypedVulnerabilities[]
  ) => void

  onChangeServers: (environments: TypedServers[], tags: TypedServers[]) => void
  onCheckVulnDetailsEnabled: (v: boolean) => void
  onChangeComplianceReports: (v: ComplianceReport[]) => void
  onCheckRouteObservationsEnabled: (v: boolean) => void
}

export function AttestationForm({
  isVulnDetailsEnabled,
  isReportConfigMissing,
  limitErrors,
  vulnTotal,
  totalRoutes,
  compliancePolicies,
  serverTags,
  vulnRules,
  vulnTags,
  vulnsSelection,
  serversSelection,
  complianceReportsSelection,
  isRouteObservationsEnabled,
  onChangeVulns,
  onChangeServers,
  onChangeComplianceReports,
  onCheckVulnDetailsEnabled,
  onCheckRouteObservationsEnabled
}: AttestationFormProps) {
  const intl = useIntl()
  const isFormDisabled = isReportConfigMissing

  const formattedTotalRoutes = intl.formatNumber(totalRoutes)
  const formattedVulnTotal = intl.formatNumber(vulnTotal)

  return (
    <>
      {isFormDisabled && isReportConfigMissing && (
        <Row>
          <Box basis={12}>
            <span style={iconCss}>
              <IconWarning color="neutral50" size="sm" />
            </span>
            <span data-testid={testIds.configMissingMsg}>
              {intl.formatMessage(
                {
                  id: 'detail_attestationReport_reportStorageConfiguration'
                },
                {
                  link: (
                    <Link
                      href={intl.formatMessage({
                        id: 'link_reportingStorage'
                      })}
                      isExternal
                    >
                      {intl.formatMessage({
                        id: 'title_attestationReport_reportConfig'
                      })}
                    </Link>
                  )
                }
              )}
            </span>
          </Box>
        </Row>
      )}
      <I18nConsumer>
        {({ intl }) => (
          <>
            <Row>
              <Box basis={12}>
                <MultiSelectCombobox<TypedVulnerabilities>
                  label={intl.formatMessage({
                    id: 'title_vulnerabilities'
                  })}
                  placeHolderTag={
                    !isFormDisabled
                      ? `${intl.formatMessage({
                          id: 'title_allRules'
                        })} (${vulnRules.length})`
                      : undefined
                  }
                  noResultsText={intl.formatMessage({
                    id: 'detail_noResultsFound'
                  })}
                  items={[
                    {
                      name: intl.formatMessage({
                        id: 'title_status'
                      }),
                      items: normalizeStatusesToTypedVulnerabilities(
                        defaultStatusRegistry.all,
                        intl
                      )
                    },
                    {
                      name: intl.formatMessage({
                        id: 'title_severityLevel'
                      }),
                      items: normalizeRuleSeveritiesToTypedVulnerabilities(
                        allRuleSeverities,
                        intl
                      )
                    },
                    {
                      name: intl.formatMessage({
                        id: 'title_assessRules'
                      }),
                      items:
                        normalizeAssessRulesToTypedVulnerabilities(vulnRules)
                    },
                    {
                      name: intl.formatMessage({
                        id: 'title_tags'
                      }),
                      items: normalizeTagsToTypedVulnerabilities(vulnTags)
                    }
                  ]}
                  itemToString={(item) => item.name}
                  itemsEqual={(first, second) =>
                    first.type === second.type && first.id === second.id
                  }
                  itemToID={(item) => `${item.type}-${item.id}`}
                  onChange={(value) => {
                    const { tags, statuses, rules, severities } =
                      normalizeTypedVulnerabilities(value)
                    onChangeVulns(severities, rules, statuses, tags)
                  }}
                  values={vulnsSelection}
                  disabled={isFormDisabled}
                />
              </Box>
            </Row>

            <Row>
              <Box basis={12}>
                <div
                  className={css`
                    margin-right: 10px;
                  `}
                >
                  <Checkbox
                    label={`${intl.formatMessage({
                      id: 'detail_includeVulnerabilityDetails'
                    })} `}
                    value={isVulnDetailsEnabled}
                    onChange={onCheckVulnDetailsEnabled}
                    disabled={isFormDisabled}
                  />
                  <Checkbox
                    label={`${intl.formatMessage({
                      id: 'detail_includeRouteObservations'
                    })} `}
                    value={isRouteObservationsEnabled}
                    onChange={onCheckRouteObservationsEnabled}
                    disabled={isFormDisabled}
                  />
                </div>
              </Box>
            </Row>

            <Row>
              <Box basis={12}>
                <MultiSelectCombobox<TypedServers>
                  label={intl.formatMessage({
                    id: 'title_environments'
                  })}
                  id="servers_multiSelectCombobox"
                  placeHolderTag={
                    !isFormDisabled
                      ? `${intl.formatMessage({
                          id: 'title_allEnvironments'
                        })} (${allServerEnvironments.length})`
                      : undefined
                  }
                  noResultsText={intl.formatMessage({
                    id: 'detail_noResultsFound'
                  })}
                  items={[
                    {
                      name: intl.formatMessage({
                        id: 'title_environment_server'
                      }),
                      items: normalizeEnvironmentsToTypedServers(
                        allServerEnvironments,
                        intl
                      )
                    },
                    {
                      name: intl.formatMessage({
                        id: 'title_tags'
                      }),
                      items: normalizeTagsToTypedServers(serverTags)
                    }
                  ]}
                  itemToString={(item) => item.name}
                  itemsEqual={(first, second) =>
                    first.type === second.type && first.id === second.id
                  }
                  itemToID={(item) => `${item.type}-${item.id}`}
                  onChange={(value) => {
                    const { tags, environments } = normalizeTypedServers(value)

                    onChangeServers(environments, tags)
                  }}
                  values={serversSelection}
                  disabled={isFormDisabled}
                />
              </Box>
            </Row>
          </>
        )}
      </I18nConsumer>
      <Row>
        <Box basis={12}>
          <MultiSelectCombobox<ComplianceReport>
            label={intl.formatMessage({
              id: 'title_securityStandards'
            })}
            id="compliance_multiSelectCombobox"
            placeHolderText={
              !isFormDisabled
                ? `${intl.formatMessage({
                    id: 'detail_choseSecurityStandards'
                  })}`
                : undefined
            }
            noResultsText={intl.formatMessage({
              id: 'detail_noResultsFound'
            })}
            items={[
              {
                items: compliancePolicies
              }
            ]}
            itemToString={(item) => item.name}
            itemToID={(item) => item.id}
            itemsEqual={(first, second) =>
              first.id === second.id && first.name === second.name
            }
            onChange={onChangeComplianceReports}
            values={complianceReportsSelection}
            disabled={isFormDisabled}
          />
          <div
            className={cx(
              metadataCss,
              css`
                margin-top: 4px;
              `
            )}
          >
            {intl.formatMessage({
              id: 'detail_applySecurityStandards'
            })}
          </div>
        </Box>
        <Box>
          <span style={iconCss}>
            <IconInformation color="neutral50" size="sm" />
          </span>
          <FormattedMessage
            id="detail_applicationAttestationReport_totalRoutesAndVulnerabilities"
            values={{
              totalRoutes: (
                <b
                  style={{
                    color:
                      containsEspecificError(
                        limitErrors,
                        'ROUTE_LIMIT_ERROR'
                      ) ||
                      containsEspecificError(
                        limitErrors,
                        'VULNS_AND_ROUTES_LIMIT_ERROR'
                      )
                        ? `${theme.color.red50}`
                        : 'inherit'
                  }}
                >
                  {formattedTotalRoutes}
                </b>
              ),
              vulnTotal: (
                <b
                  style={{
                    color:
                      containsEspecificError(limitErrors, 'VULS_LIMIT_ERROR') ||
                      containsEspecificError(
                        limitErrors,
                        'VULNS_AND_ROUTES_LIMIT_ERROR'
                      )
                        ? `${theme.color.red50}`
                        : 'inherit'
                  }}
                >
                  {formattedVulnTotal}
                </b>
              )
            }}
          />
        </Box>
        {limitErrors.map((error) => (
          <Box key={error.errorType} className={errorStateMessageCss}>
            <span style={iconCss}>
              <IconWarning color="red50" size="md" />
            </span>
            {intl.formatMessage(
              {
                id: error.error
              },
              {
                limit: error.limit,
                total: error.total
              }
            )}
          </Box>
        ))}
      </Row>
    </>
  )
}

function containsEspecificError(
  limitsErrors: LimitsReportError[],
  errorType: ErrorLimit
) {
  return limitsErrors.some((error) => error.errorType === errorType)
}

function normalizeTypedVulnerabilities(vulns: TypedVulnerabilities[]) {
  const rules: TypedVulnerabilities[] = []
  const severities: TypedVulnerabilities[] = []
  const statuses: TypedVulnerabilities[] = []
  const tags: TypedVulnerabilities[] = []

  vulns.forEach((vuln) => {
    if (vuln.type === 'rule') {
      rules.push(vuln)
    } else if (vuln.type === 'severity') {
      severities.push(vuln)
    } else if (vuln.type === 'status') {
      statuses.push(vuln)
    } else if (vuln.type === 'tags') {
      tags.push(vuln)
    }
  })

  return { rules, severities, statuses, tags }
}

function normalizeTypedServers(servers: TypedServers[]) {
  const tags: TypedServers[] = []
  const environments: TypedServers[] = []

  servers.forEach((server) => {
    if (server.type === 'tags') {
      tags.push(server)
    } else if (server.type === 'environment') {
      environments.push(server)
    }
  })

  return { tags, environments }
}
